import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, addDoc, getDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { PlusIcon, ExclamationIcon } from '../../components/icons/Icons';
import { sendNotificationEmail } from '../../firebase/notifications';

// This component provides the UI for submitting invoices against approved purchase requests.
export default function SubmitInvoice({ userProfile, showModal, appId }) {
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [existingInvoices, setExistingInvoices] = useState([]);
    // ✅ 1. Added state for search and sort
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        
        const requestsCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests`;
        let requestsQuery;

        if (userProfile.role === 'Admin') {
            requestsQuery = query(
                collection(db, requestsCollectionPath),
                where("status", "==", "Approved")
            );
        } else {
            requestsQuery = query(
                collection(db, requestsCollectionPath),
                where("status", "==", "Approved"),
                where("department", "==", userProfile.department)
            );
        }

        const unsubscribe = onSnapshot(requestsQuery, (querySnapshot) => {
            const requestsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setApprovedRequests(requestsData);
            setIsLoading(false);
        }, (err) => {
            console.error("Firestore snapshot error:", err);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [userProfile, appId]);

    const handleOpenInvoiceModal = (request) => {
        const invoicesCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests/${request.id}/invoices`;
        const q = query(collection(db, invoicesCollectionPath));
        onSnapshot(q, (snapshot) => {
            setExistingInvoices(snapshot.docs.map(doc => doc.data()));
            setSelectedRequest(request);
            setIsModalOpen(true);
        });
    };

    const handleInvoiceSubmit = async ({ amount, dueDate, invoiceFile, comments, assignedApproverId }) => {
        if (!storage || !userProfile) {
            showModal("Error", "Database not connected.");
            return;
        }
        
        try {
            const filePath = `attachments/invoices/${userProfile.uid}/${Date.now()}-${invoiceFile.name}`;
            const fileRef = ref(storage, filePath);
            await uploadBytes(fileRef, invoiceFile);
            const downloadURL = await getDownloadURL(fileRef);
            const uploadedFileObject = { name: invoiceFile.name, url: downloadURL, path: filePath };

            const newInvoiceNumber = `${selectedRequest.requestNumber}-INV-${String(existingInvoices.length + 1).padStart(2, '0')}`;
            const vendorName = selectedRequest.vendorType === 'new' ? selectedRequest.newVendorName : selectedRequest.selectedVendor;

            const invoiceData = {
                purchaseRequestId: selectedRequest.id,
                requestNumber: selectedRequest.requestNumber,
                invoiceNumber: newInvoiceNumber,
                amount: parseFloat(amount),
                dueDate: new Date(dueDate),
                invoiceFile: uploadedFileObject,
                status: 'Pending Invoice Approval',
                submittedBy: userProfile.name,
                submittedById: userProfile.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                assignedApproverId: assignedApproverId, 
                vendorName: vendorName, 
                currency: selectedRequest.currency,
                billingEntity: selectedRequest.billingEntity,
                comments: comments || '', 
            };

            const invoicesCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests/${selectedRequest.id}/invoices`;
            await addDoc(collection(db, invoicesCollectionPath), invoiceData);
            
            setIsModalOpen(false);
            setSelectedRequest(null);
        } catch (e) {
            console.error("Error submitting invoice:", e);
            showModal("Error", "Failed to submit invoice.");
        }
    };

    // ✅ 2. Combined filtering and sorting logic
    const processedRequests = useMemo(() => {
        let filtered = [...approvedRequests];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(req => {
                const vendor = req.vendorType === 'new' ? req.newVendorName : req.selectedVendor;
                return req.requestNumber.toLowerCase().includes(lowercasedTerm) ||
                       vendor.toLowerCase().includes(lowercasedTerm) ||
                       req.description.toLowerCase().includes(lowercasedTerm);
            });
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (sortConfig.key === 'createdAt' && a.createdAt?.toDate) {
                aValue = a.createdAt.toDate();
                bValue = b.createdAt.toDate();
            }
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [approvedRequests, searchTerm, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            {isModalOpen && (
                <AddInvoiceModal 
                    request={selectedRequest} 
                    invoiceCount={existingInvoices.length}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleInvoiceSubmit}
                    appId={appId}
                    userProfile={userProfile}
                />
            )}
            <div>
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Submit an Invoice</h2>
                
                {/* ✅ 3. Added search bar */}
                <div className="mb-4">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by PR number, vendor, or description..."
                        className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                    />
                </div>

                {/* ✅ 4. Refactored into a table with sortable headers */}
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('requestNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">PR Number</th>
                                <th onClick={() => requestSort('selectedVendor')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                <th onClick={() => requestSort('description')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Description</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {processedRequests.length > 0 ? (
                                processedRequests.map(req => (
                                    <ExpandableRequestRow 
                                        key={req.id}
                                        request={req}
                                        onAddInvoiceClick={handleOpenInvoiceModal}
                                        appId={appId}
                                        showModal={showModal}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-12 px-6">
                                        <h3 className="text-sm font-medium text-[#213368]">No Approved Requests</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {searchTerm ? "Your search returned no results." : "There are no approved purchase requests to submit an invoice against."}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

function ExpandableRequestRow({ request, onAddInvoiceClick, appId, showModal }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

    const isExpired = request.engagementEndDate && new Date(request.engagementEndDate.toDate()) < new Date();
    const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.amount, 0);
    const isOverBudget = totalInvoiced > request.approxValue;

    const toggleExpand = () => {
        if (!isExpanded) {
            setIsLoadingInvoices(true);
            const invoicesCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests/${request.id}/invoices`;
            const q = query(collection(db, invoicesCollectionPath));
            onSnapshot(q, (snapshot) => {
                setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setIsLoadingInvoices(false);
            });
        }
        setIsExpanded(!isExpanded);
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Paid': return 'bg-blue-100 text-blue-800';
            case 'Approved for Payment': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleExpiredClick = () => {
        showModal("Request Expired", "This purchase request has expired. Please raise a new purchase request to submit an invoice.");
    };

    return (
        <>
             {/* --- FIX: The table row is now correctly structured --- */}
            <tr className="cursor-pointer hover:bg-gray-50" onClick={toggleExpand}>
                {/* Column 1: PR Number */}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-[#D95448] flex items-center">
                    {isOverBudget && <span title="Total invoiced amount exceeds approved purchase value."><ExclamationIcon /></span>}
                    <span className="ml-2">{request.requestNumber}</span>
                </td>
                
                {/* Column 2: Vendor */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 truncate max-w-xs">
                    {request.vendorType === 'new' ? request.newVendorName : request.selectedVendor}
                </td>
                
                {/* Column 3: Description (with scrolling) */}
                <td 
                    title={request.description} 
                    className="px-6 py-4 text-sm text-[#213368] max-w-xs max-h-24 overflow-y-auto"
                >
                    {request.description}
                </td>

                {/* Column 4: Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                        onClick={(e) => { e.stopPropagation(); onAddInvoiceClick(request); }}
                        disabled={isExpired} 
                        title={isExpired ? "This purchase request has expired." : "Add a new invoice"}
                        className="flex items-center justify-center ml-auto px-3 py-1 bg-[#20BEC6] text-white text-sm rounded-md shadow-sm hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        <PlusIcon /> <span className="ml-1">Add Invoice</span>
                    </button>
                </td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan="4" className="p-4 bg-gray-50">
                        {isLoadingInvoices ? <LoadingSpinner /> : (
                            invoices.length > 0 ? (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice ID</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {invoices.map(inv => (
                                            <tr key={inv.id}>
                                                <td className="px-4 py-2 text-sm font-mono">{inv.invoiceNumber}</td>
                                                <td className="px-4 py-2 text-sm">{request.currency} {inv.amount}</td>
                                                <td className="px-4 py-2 text-sm">{inv.dueDate.toDate().toLocaleDateString()}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(inv.status)}`}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No invoices have been submitted for this purchase request yet.</p>
                            )
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

function AddInvoiceModal({ request, invoiceCount, onClose, onSubmit, appId, userProfile }) {
    const [isSubmitting, setIsSubmitting] = useState(false);    
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [comments, setComments] = useState('');
    const [assignedApproverId, setAssignedApproverId] = useState('');
    const [approvers, setApprovers] = useState([]);
    const [error, setError] = useState('');

    // ✅ 5. Corrected and simplified approver fetching logic
    useEffect(() => {
        if (!appId || !userProfile) return;

        const usersCollectionPath = `/artifacts/${appId}/users`;
        const approversQuery = query(collection(db, usersCollectionPath), where("role", "in", ["Admin", "Approver"]));

        const unsubscribe = onSnapshot(approversQuery, (snapshot) => {
            const approversData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Filter out the current user from the list of potential approvers
            setApprovers(approversData.filter(approver => approver.uid !== userProfile.uid));

            // Pre-select the original approver if they are not the current user
            const originalApprover = approversData.find(a => a.name === request.approvedBy);
            if (originalApprover && originalApprover.uid !== userProfile.uid) {
                setAssignedApproverId(originalApprover.id);
            }
        });

        return () => unsubscribe();
    }, [appId, userProfile, request.approvedBy]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!amount || !dueDate || !invoiceFile || !assignedApproverId) {
            setError('Please fill all fields, select an approver, and upload the invoice file.');
            return;
        }
        setError('');

        // Start the loader
        setIsSubmitting(true);
        
        try {
            // Wait for the parent's submit logic (including file upload) to complete
            await onSubmit({ amount, dueDate, invoiceFile, comments, assignedApproverId });
        } catch (err) {
            // The parent component already shows an error modal, but we log here too
            console.error("Submission failed in modal:", err);
        } finally {
            // Stop the loader regardless of success or failure
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Add Invoice for {request.requestNumber}</h2>
                <p className="text-sm text-gray-500 mb-4">This will be invoice #{invoiceCount + 1} for this purchase.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Invoice Amount ({request.currency})</label>
                        <input 
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Due Date</label>
                        <input 
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Approver</label>
                        <select value={assignedApproverId} onChange={e => setAssignedApproverId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6]">
                            <option value="">Select an approver...</option>
                            {approvers.map(approver => (
                                <option key={approver.id} value={approver.id}>{approver.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Upload Invoice (PDF/Image)</label>
                        <input 
                            type="file"
                            onChange={(e) => setInvoiceFile(e.target.files[0])}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20BEC6] file:bg-opacity-20 file:text-[#20BEC6] hover:file:bg-opacity-30"
                            accept="image/*,application/pdf"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Comments</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows="3"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            placeholder="Optional comments for the approver..."
                        ></textarea>
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className="flex justify-center items-center px-6 py-2 w-48 bg-[#20BEC6] text-white font-medium rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <LoadingSpinner size="sm" />
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ✅ New component for the expired alert
function ExpiredRequestAlert() {
    return (
        <div className="flex items-center justify-end">
            <span className="px-3 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                Expired
            </span>
        </div>
    );
}
